import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
} from "react";

// Chat states
const CHAT_MODES = {
  SIDEBAR: "sidebar",
  POPUP: "popup",
  NEWWINDOW: "newwindow",
  CLOSED: "closed",
};

// Action types
const CHAT_ACTIONS = {
  SET_MESSAGES: "SET_MESSAGES",
  ADD_MESSAGE: "ADD_MESSAGE",
  UPDATE_MESSAGE: "UPDATE_MESSAGE",
  REMOVE_MESSAGE: "REMOVE_MESSAGE",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  SET_MODE: "SET_MODE",
  SET_CONVERSATION_ID: "SET_CONVERSATION_ID",
  SET_INITIAL_SUGGESTIONS: "SET_INITIAL_SUGGESTIONS",
  RESET_CHAT: "RESET_CHAT",
  SET_INPUT_DRAFT: "SET_INPUT_DRAFT",
};

// Initial state
const initialState = {
  messages: [],
  loading: false,
  error: null,
  mode: CHAT_MODES.CLOSED,
  conversationID: null,
  initialSuggestions: [],
  inputDraft: "",
  // Popup specific state
  popupPosition: { x: window.innerWidth - 400, y: 100 },
  popupSize: { width: 360, height: 600 },
};

// Reducer
function chatReducer(state, action) {
  switch (action.type) {
    case CHAT_ACTIONS.SET_MESSAGES:
      return { ...state, messages: action.payload };

    case CHAT_ACTIONS.ADD_MESSAGE:
      return {
        ...state,
        messages: [
          ...state.messages,
          { ...action.payload, id: action.payload.id || Date.now() },
        ],
      };

    case CHAT_ACTIONS.UPDATE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === action.payload.id
            ? { ...msg, ...action.payload.updates }
            : msg
        ),
      };

    case CHAT_ACTIONS.REMOVE_MESSAGE:
      return {
        ...state,
        messages: state.messages.filter((msg) => msg.id !== action.payload.id),
      };

    case CHAT_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };

    case CHAT_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };

    case CHAT_ACTIONS.SET_MODE:
      return { ...state, mode: action.payload };

    case CHAT_ACTIONS.SET_CONVERSATION_ID:
      return { ...state, conversationID: action.payload };

    case CHAT_ACTIONS.SET_INITIAL_SUGGESTIONS:
      return { ...state, initialSuggestions: action.payload };

    case CHAT_ACTIONS.SET_INPUT_DRAFT:
      return { ...state, inputDraft: action.payload };

    case CHAT_ACTIONS.RESET_CHAT:
      return {
        ...state,
        messages: [
          {
            id: 1,
            sender: "ai",
            text: "<p>Hey there!<br/><br/>Need answers about your document?<br/>I've got you covered!</p>",
            suggestions:
              action.payload?.initialSuggestions || state.initialSuggestions,
            conversationID:
              action.payload?.conversationID || state.conversationID,
          },
        ],
        loading: false,
        error: null,
        inputDraft: "",
      };

    default:
      return state;
  }
}

// Context
const ChatContext = createContext();

// Provider component
export function ChatProvider({ children, initialState: customInitialState }) {
  const [state, dispatch] = useReducer(
    chatReducer,
    customInitialState || initialState
  );

  // Helper function to get localStorage key for input drafts
  const getInputKey = useCallback(
    (conversationID) => `sidebarChatInput_${conversationID}`,
    []
  );

  // Actions
  const actions = {
    // Message actions
    setMessages: useCallback((messages) => {
      dispatch({ type: CHAT_ACTIONS.SET_MESSAGES, payload: messages });
    }, []),

    addMessage: useCallback((message) => {
      dispatch({ type: CHAT_ACTIONS.ADD_MESSAGE, payload: message });
    }, []),

    updateMessage: useCallback((id, updates) => {
      dispatch({ type: CHAT_ACTIONS.UPDATE_MESSAGE, payload: { id, updates } });
    }, []),

    removeMessage: useCallback((id) => {
      dispatch({ type: CHAT_ACTIONS.REMOVE_MESSAGE, payload: { id } });
    }, []),

    // State actions
    setLoading: useCallback((loading) => {
      dispatch({ type: CHAT_ACTIONS.SET_LOADING, payload: loading });
    }, []),

    setError: useCallback((error) => {
      dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: error });
    }, []),

    setMode: useCallback((mode) => {
      dispatch({ type: CHAT_ACTIONS.SET_MODE, payload: mode });
    }, []),

    setConversationID: useCallback((conversationID) => {
      dispatch({
        type: CHAT_ACTIONS.SET_CONVERSATION_ID,
        payload: conversationID,
      });
    }, []),

    setInitialSuggestions: useCallback((suggestions) => {
      dispatch({
        type: CHAT_ACTIONS.SET_INITIAL_SUGGESTIONS,
        payload: suggestions,
      });
    }, []),

    // Input draft management (with localStorage)
    setInputDraft: useCallback(
      (draft) => {
        dispatch({ type: CHAT_ACTIONS.SET_INPUT_DRAFT, payload: draft });
        // Save to localStorage
        try {
          if (state.conversationID) {
            const key = getInputKey(state.conversationID);
            if (draft && draft.trim()) {
              localStorage.setItem(key, draft);
            } else {
              localStorage.removeItem(key);
            }
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      },
      [state.conversationID, getInputKey]
    ),

    loadInputDraft: useCallback(
      (conversationID) => {
        try {
          const key = getInputKey(conversationID);
          const saved = localStorage.getItem(key);
          if (saved) {
            dispatch({ type: CHAT_ACTIONS.SET_INPUT_DRAFT, payload: saved });
            return saved;
          }
        } catch (e) {
          // Ignore localStorage errors
        }
        return "";
      },
      [getInputKey]
    ),

    clearInputDraft: useCallback(
      (conversationID) => {
        try {
          const key = getInputKey(conversationID || state.conversationID);
          localStorage.removeItem(key);
        } catch (e) {
          // Ignore localStorage errors
        }
        dispatch({ type: CHAT_ACTIONS.SET_INPUT_DRAFT, payload: "" });
      },
      [state.conversationID, getInputKey]
    ),

    // Reset chat
    resetChat: useCallback(
      (options = {}) => {
        dispatch({
          type: CHAT_ACTIONS.RESET_CHAT,
          payload: {
            conversationID: options.conversationID || state.conversationID,
            initialSuggestions:
              options.initialSuggestions || state.initialSuggestions,
          },
        });
      },
      [state.conversationID, state.initialSuggestions]
    ),

    // Mode helpers
    openSidebar: useCallback(() => {
      dispatch({ type: CHAT_ACTIONS.SET_MODE, payload: CHAT_MODES.SIDEBAR });
    }, []),

    openPopup: useCallback(() => {
      dispatch({ type: CHAT_ACTIONS.SET_MODE, payload: CHAT_MODES.POPUP });
    }, []),

    openNewWindow: useCallback(() => {
      dispatch({ type: CHAT_ACTIONS.SET_MODE, payload: CHAT_MODES.NEWWINDOW });
    }, []),

    closeChat: useCallback(() => {
      dispatch({ type: CHAT_ACTIONS.SET_MODE, payload: CHAT_MODES.CLOSED });
    }, []),

    // Check if chat is locked (prevent multiple simultaneous operations)
    isChatLocked: useCallback(() => {
      return state.loading;
    }, [state.loading]),
  };

  const value = {
    ...state,
    ...actions,
    CHAT_MODES,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

// Hook to use chat context
export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}

export { CHAT_MODES };
